import PropTypes from 'prop-types';
import React, {
  useCallback, useEffect, useRef, useState,
} from 'react';
import { X } from 'react-feather';
import clsx from 'clsx';
import Button, {
  ButtonAppearance,
  ButtonSizes,
  ButtonTypes,
} from '../button/button';
import Icon from '../icon/icon';
import Input from '../input';
import List from '../list';
import { ListItemDataPropTypes, SelectionStyles } from '../list/list-item';
import Popper from '../popper';
import './auto-complete.scss';

const AutoComplete = ({
  label,
  inputValue,
  onInputValueChange,
  items,
  onItemClick,
  onClose,
  disabled,
  readOnly,
  onCrossClick,
  showCross,
  leftAddOn,
  rightAddOn,
  closeOnClick,
  hintText,
  invalid,
  footerActions,
  placeholder,
  // loading,
  selectionStyle,
}) => {
  const inputRef = useRef(null);
  const [openPopper, setOpenPopper] = useState(false);
  const [inpValue, setInpValue] = useState(inputValue);

  useEffect(() => {
    setInpValue(inputValue);
  }, [inputValue]);

  useEffect(() => {
    if (document.activeElement === inputRef.current) {
      if (items) {
        setOpenPopper(true);
      }
    }
  }, [items]);

  const handleInputValueChange = (event) => {
    const { value } = event.target;
    setInpValue(value);
    onInputValueChange(value);
  };

  const handleSelectionClear = () => {
    setInpValue('');
    if (onCrossClick && typeof onCrossClick === 'function') {
      onCrossClick();
    }
  };

  const handleClose = () => {
    if (onClose && typeof onClose === 'function') {
      onClose();
    }
    setOpenPopper(false);
  };

  const handleItemClick = (id, isSelected, selectedObj) => {
    if (onItemClick && typeof onItemClick === 'function') {
      onItemClick.call(null, id, isSelected, selectedObj);
    }
    if (closeOnClick) {
      handleClose();
    }
  };

  const renderIcon = ({ size }) => <Icon icon={X} size={size} />;

  const handleOpen = useCallback(() => {
    if (items) {
      setOpenPopper(true);
    }
  }, [items]);

  const getSuffix = () => (showCross ? (
    <Button
      renderIcon={renderIcon}
      type={ButtonTypes.LINK}
      appearance={ButtonAppearance.TEXT}
      onClick={handleSelectionClear}
      size={ButtonSizes.SMALL}
    />
  ) : null);

  const handleFooterAction = ({ onClick }) => () => {
    if (onClick && typeof onClick === 'function') {
      const closePopper = onClick.call(null) ?? false;
      if (closePopper) {
        handleClose();
      }
    }
  };

  const getPopperView = () => (
    <>
      {items && items.length >= 0 && (
      <List
        items={items}
        onItemClick={handleItemClick}
        listItemProps={{ selectionStyle }}
      />
      )}
      {footerActions.length > 0
      && footerActions.map((action) => (
        <Button
              // eslint-disable-next-line react/jsx-props-no-spreading
          {...action}
          className={clsx('footer-action', action.className)}
          onClick={handleFooterAction(action)}
        />
      ))}
    </>
  );

  const renderPopper = () => {
    if (readOnly) {
      return (
        <Input
          ref={inputRef}
          value={inpValue}
          isDisabled={disabled}
          isReadOnly={readOnly}
          leftAddOn={leftAddOn}
          rightAddOn={rightAddOn}
          hintText={hintText}
          isInvalid={invalid}
          placeholder={placeholder}
        />
      );
    }

    return (
      <Popper
        open={openPopper}
        view={getPopperView()}
        onClose={handleClose}
        onOpen={handleOpen}
      >
        <div>
          <Input
            ref={inputRef}
            onChange={handleInputValueChange}
            value={inpValue}
            isDisabled={disabled}
            isReadOnly={readOnly}
            leftAddOn={leftAddOn}
            rightAddOn={rightAddOn}
            hintText={hintText}
            isInvalid={invalid}
            placeholder={placeholder}
            suffix={getSuffix()}
          />
        </div>
      </Popper>
    );
  };

  return (
    <div className="autocomplete-cont">
      {label && <div className="autocomplete-label">{label}</div>}
      {renderPopper()}
    </div>
  );
};

AutoComplete.SelectionStyles = SelectionStyles;

AutoComplete.propTypes = {
  label: PropTypes.string,
  inputValue: PropTypes.string,
  onInputValueChange: PropTypes.func,
  items: PropTypes.oneOf([null, [], PropTypes.arrayOf(PropTypes.shape(ListItemDataPropTypes))]),
  onItemClick: PropTypes.func,
  onClose: PropTypes.func,
  disabled: PropTypes.bool,
  readOnly: PropTypes.bool,
  closeOnClick: PropTypes.bool,
  onCrossClick: PropTypes.func,
  showCross: PropTypes.bool,
  leftAddOn: PropTypes.oneOfType([PropTypes.string, PropTypes.element]),
  rightAddOn: PropTypes.oneOfType([PropTypes.string, PropTypes.element]),
  invalid: PropTypes.bool,
  hintText: PropTypes.string,
  footerActions: PropTypes.arrayOf(
    PropTypes.shape({
      label: PropTypes.string,
      title: PropTypes.string,
      type: PropTypes.oneOf(Object.values(ButtonTypes)),
      size: PropTypes.oneOf(Object.values(ButtonSizes)),
      renderIcon: PropTypes.func,
      disabled: PropTypes.bool,
      className: PropTypes.string,
      onClick: PropTypes.func,
      borderLess: PropTypes.bool,
    }),
  ),
  placeholder: PropTypes.string,
  loading: PropTypes.bool,
  selectionStyle: PropTypes.oneOf(Object.values(SelectionStyles)),
};

AutoComplete.defaultProps = {
  label: '',
  inputValue: '',
  items: [],
  onInputValueChange: () => {},
  onItemClick: () => {},
  onClose: () => {},
  disabled: false,
  readOnly: false,
  closeOnClick: true,
  onCrossClick: () => {},
  showCross: false,
  leftAddOn: null,
  rightAddOn: null,
  invalid: false,
  hintText: '',
  footerActions: [],
  placeholder: '',
  loading: false,
  selectionStyle: SelectionStyles.TICK,
};

export { AutoComplete as default };
